/**
 * [LGP 2026-10-15, user] NEW FILE — the "Lead Generator Performance" tab (right after Team Sameer Report): the Apps Script
 * dashboard of the same name (Downloads/Leads Generated by Analysis) on our database. User: "i just want the exact same thing
 * on my application, layout can be different but the logic process, each and every thing will be the same and now it will use
 * the database and not the sheet one". Its five pages are the five views of the switch (the script's dock): Leads Count ·
 * Monthly Performance · NAL Leads · Team Roster · Bonus Rules (pages/lgp/*). The script's own sign-in is not needed — the app's
 * sign-in and this tab's grant replace it (the server checks the grant on every call).
 *
 * Freshness, as the script ([JavaScript.html] noteVersion / PAGE_DEPS / pollStatus): every response carries the data version
 * '<leads data>|r<roster>|b<bonus rules>'; when one moves, every view already opened that depends on that part is fetched again
 * in the background (Leads Count ← all three · Monthly Performance ← leads data + roster · NAL Leads ← leads data · Team Roster ←
 * roster · Bonus Rules ← bonus rules). The version is asked every 15 s while the tab is on show, and at once when anyone saves a
 * roster or bonus change (the app's realtime 'leadGenPerf'). A roster edit or bonus save moves the person / the bonuses on Leads
 * Count (and the person's side on Monthly Performance) at once, before the save returns.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { SlideViewport, SlidePane, slidePos } from '../components/ViewSlide';
import { InstantPick, PickHandlers } from '../components/InstantPick'; // [LGP-PILL-INSTANT 2026-11-02]
import PureInk from '../components/PureInk'; // [PURE-INK 2026-10-15]
import { useRealtime } from '../hooks/useRealtime';
import { useAuth } from '../contexts/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile'; // [PHONE-LAYOUT 2026-10-23]
import { fetchLgpStatus } from '../services/api';
import { pillTrack, pillSegment } from '../styles/buttons';
import LeadsCountView from './lgp/LeadsCountView';
import MonthlyView from './lgp/MonthlyView';
import NalView from './lgp/NalView';
import RosterView from './lgp/RosterView';
import BonusView from './lgp/BonusView';
import { dark, parseVersion, ViewId, ViewHandle, PersonPatch } from './lgp/lgpShared';
import { leadsNoDock } from '../config/sections'; // [TL-REPORT-ONLY 2026-10-19]
import { layoutMax } from '../utils/appZoom'; // [APP-FIT 2026-10-26] laptop width rules follow the laid-out width

/** [JavaScript.html PAGES] */
const VIEWS: { id: ViewId; label: string }[] = [
  { id: 'leads-count', label: 'Leads Count' },
  { id: 'leads-analysis', label: 'Monthly Performance' },
  { id: 'nal-records', label: 'NAL Leads' },
  { id: 'team-roster', label: 'Team Roster' },
  { id: 'bonus-rules', label: 'Bonus Rules' },
];
const ORDER = VIEWS.map((v) => v.id);
/** [JavaScript.html PAGE_DEPS] What each view's data is built from: ds = the leads data, r = the roster, b = the bonus rules. */
const PAGE_DEPS: Record<ViewId, { ds?: true; r?: true; b?: true }> = {
  'leads-count': { ds: true, r: true, b: true },
  'leads-analysis': { ds: true, r: true },
  'nal-records': { ds: true },
  'team-roster': { r: true },
  'bonus-rules': { b: true },
};
/** The script polls every 5 s; the app's realtime makes roster / bonus changes instant, so the leads data is asked every 15 s. */
const POLL_MS = 15_000;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding: 4px 2px 0;
  color: ${(p) => p.theme.text}; /* [PURE-INK] what has no colour of its own reads the pure ink too */

  /* [PHONE-LAYOUT 2026-10-23, user] phones: NOTHING is fixed at the top — the whole tab (title, view switch, the view's
     dates, tiles, search and table) is ONE scroll, as Lead Management ("in the phone view nothing will be fixed on the top
     side otherwise how will i see the data for the below"). The views take their full height (Viewport below; lgpShared
     ViewWrap / TableScroll). */
  @media (max-width: 768px) {
    gap: 10px;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
  }
`;
/* The Payments / Lead Management top strip: the title left, the view switch in the middle. */
const TopBar = styled.div`
  flex-shrink: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 16px;
  margin: -4px -2px 0;
  padding: 12px 20px;
  background: ${(p) => (dark(p)
    ? 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.04) 100%)'
    : 'linear-gradient(180deg, rgba(241,245,249,0.85) 0%, rgba(229,232,231,0.72) 100%)')};
  border-bottom: 1px solid ${(p) => (dark(p) ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.10)')};
  box-shadow: inset 0 1px 0 ${(p) => (dark(p) ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)')};

  ${layoutMax(1100)} {
    grid-template-columns: minmax(0, 1fr);
    justify-items: center;
    gap: 10px;
  }
  /* [PHONE-LAYOUT 2026-10-23] phones: Lead Management's phone strip padding; the empty third cell (the laptop's right
     column) takes no line */
  @media (max-width: 768px) {
    padding: 10px 12px;
    & > span:empty { display: none; }
  }
`;
const Title = styled.h1`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  ${layoutMax(1100)} { text-align: center; }
  /* [PHONE-LAYOUT 2026-10-23] phones: the shared phone page title (Lead Management's), 17 px heavy */
  @media (max-width: 768px) { font-size: 17px; font-weight: 800; }
`;
const Viewport = styled(SlideViewport)`
  flex: 1;
  min-height: 0;
  padding: 0 14px;

  & > [data-lgp-pane] {
    height: 100%;
  }

  /* [PHONE-LAYOUT 2026-10-23, user] phones: the view on show at its full height (the page scrolls); 8 px sides (the view's
     bars add their own 4 px) so the tables get the width */
  @media (max-width: 768px) {
    flex: none;
    min-height: auto;
    padding: 0 8px;
    & > [data-lgp-pane] { height: auto; }
  }
`;

/* The app's pill with the sliding knob (Lead Management's KnobPill look). */
const Pill = styled.div`
  position: relative;
  display: inline-flex;
  max-width: 100%;
  ${pillTrack}
  /* [LIQUID-GLASS 2026-11-02, user] the track is pressed into the bar; the knob is glossy and slides the theme switch's way */
  background: ${(p) => (dark(p) ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
  border: 1px solid transparent;
  box-shadow: ${(p) => (dark(p)
    ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
    : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.08)')};
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }

  /* [PHONE-LAYOUT 2026-10-23] phones: the pill keeps its full length and its row (PillRow) swipes instead — the track runs
     on past the screen's edge with the next view's name peeking (Lead Management's view switch). Was: the pill scrolled
     inside itself, cut at "NA…", and the view picked could sit off its edge. */
  @media (max-width: 768px) {
    flex: none;
    max-width: none;
    overflow: visible;
    margin: 0 auto;
  }
`;
/* [PHONE-LAYOUT 2026-10-23] phones only: the view switch's row — it swipes sideways (no scroll bar shown) and the view picked
   is brought into its middle (ViewPill). Never drawn on a laptop. */
const PillRow = styled.div`
  justify-self: stretch;
  width: 100%;
  min-width: 0;
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;
const Knob = styled.div<{ $left: number; $width: number; $ready: boolean }>`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 0;
  width: ${(p) => p.$width}px;
  transform: translateX(${(p) => p.$left}px);
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0) 55%), ${(p) => (dark(p) ? '#4acd8b' : p.theme.green600)};
  box-shadow: 0 2px 8px ${(p) => (dark(p) ? 'rgba(74,205,139,0.30)' : 'rgba(20,107,84,0.30)')}, inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(0,0,0,0.12);
  /* [LIQUID-GLASS 2026-11-02] the theme switch's slide (0.42 s, the same curve); off until it has been drawn in place once */
  transition: ${(p) => (p.$ready ? 'transform 0.42s cubic-bezier(0.32, 0.72, 0, 1), width 0.42s cubic-bezier(0.32, 0.72, 0, 1)' : 'none')};
  opacity: ${(p) => (p.$width ? 1 : 0)};
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;
const Opt = styled.button<{ $active: boolean }>`
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  border: none;
  background: transparent;
  cursor: pointer;
  ${pillSegment}
  color: ${(p) => (p.$active ? (dark(p) ? '#06281d' : '#ffffff') : p.theme.muted)};
  transition: color 180ms cubic-bezier(0.4, 0, 0.2, 1);
`;
/** [LGP-PILL-INSTANT 2026-11-02] The knob moves at the mouse PRESS (components/InstantPick, as Lead Management's switch and the
 *  theme switch); the view follows a moment later as a low-priority update, so a heavy first open (Monthly Performance's table)
 *  can no longer hold the knob still — measured: it stood still for the first open, then jumped. */
function ViewPill({ value, onPick }: { value: ViewId; onPick: (v: ViewId) => void }) {
  return <InstantPick value={value} onPick={onPick}>{(shown, press) => <ViewPillTrack value={shown} press={press} />}</InstantPick>;
}
function ViewPillTrack({ value, press }: { value: ViewId; press: (v: ViewId) => PickHandlers }) {
  const isMobile = useIsMobile(); // [PHONE-LAYOUT 2026-10-23] the phone's sideways switch (below)
  const pillRef = useRef<HTMLDivElement>(null);
  const refs = useRef<Partial<Record<ViewId, HTMLButtonElement | null>>>({});
  const [knob, setKnob] = useState({ left: 0, width: 0 });
  const [ready, setReady] = useState(false); // [LIQUID-GLASS 2026-11-02] no slide-in when the tab first shows
  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const measure = useCallback(() => {
    const el = refs.current[value];
    if (el) setKnob((k) => (k.left === el.offsetLeft && k.width === el.offsetWidth ? k : { left: el.offsetLeft, width: el.offsetWidth }));
  }, [value]);
  useLayoutEffect(() => { measure(); }, [measure]);
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !pillRef.current) return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(pillRef.current);
    return () => ro.disconnect();
  }, [measure]);
  /* [PHONE-LAYOUT 2026-10-23] phones: the five views do not fit one phone line, so the switch's row swipes — the view picked
     is brought into the middle of it (on the press and when the tab opens), as Lead Management's. The laptop never runs this. */
  useEffect(() => {
    if (!isMobile) return;
    const row = pillRef.current?.parentElement; // PillRow — the row that swipes on a phone
    const el = refs.current[value];
    if (!row || !el || row.scrollWidth <= row.clientWidth + 1) return;
    const rr = row.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    const left = Math.max(0, row.scrollLeft + (er.left - rr.left) - (row.clientWidth - er.width) / 2);
    if (typeof row.scrollTo === 'function') row.scrollTo({ left, behavior: 'smooth' }); else row.scrollLeft = left;
  }, [isMobile, value]);
  return (
    <Pill ref={pillRef} role="tablist" aria-label="Lead Generator Performance view" data-lgp-pill="">
      <Knob $left={knob.left} $width={knob.width} $ready={ready} aria-hidden="true" />
      {VIEWS.map((v) => (
        <Opt key={v.id} ref={(el) => { refs.current[v.id] = el; }} type="button" role="tab" aria-selected={v.id === value} $active={v.id === value}
          {...press(v.id)} data-lgp-tab={v.id}>{v.label}</Opt>
      ))}
    </Pill>
  );
}

const LeadGeneratorPerformancePage: React.FC = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile(); // [PHONE-LAYOUT 2026-10-23] the view switch in its swipe row (PillRow) on phones
  const storageKey = String(user?.id ?? 'anon');
  // [LEADS-NO-DOCK] the Leads type has no dock (AppLayout isLeadsOnly): the tables run down to the card's bottom
  const noDock = leadsNoDock(user); // [TL-REPORT-ONLY 2026-10-19] the same rule as AppLayout's dock
  const [view, setView] = useState<ViewId>('leads-count');
  const handles = useRef<Partial<Record<ViewId, ViewHandle>>>({});
  const known = useRef<{ ds: string; r: number; b: number } | null>(null);

  const register = useCallback((id: ViewId, h: ViewHandle) => { handles.current[id] = h; }, []);

  /** [JavaScript.html noteVersion] Parts only move forward; every opened view that depends on a part that moved is fetched
   *  again — not just the one on screen — except the view whose response this was (already current). */
  const noteVersion = useCallback((v: string, fromPage?: ViewId) => {
    if (!v) return;
    const next = parseVersion(v);
    const k = known.current;
    if (k === null) { known.current = next; return; }        // first contact
    const moved = { ds: next.ds > k.ds, r: next.r > k.r, b: next.b > k.b };
    if (!moved.ds && !moved.r && !moved.b) return;
    known.current = { ds: moved.ds ? next.ds : k.ds, r: Math.max(next.r, k.r), b: Math.max(next.b, k.b) };
    for (const id of ORDER) {
      if (id === fromPage) continue;
      const h = handles.current[id];
      if (!h) continue;                                       // not opened yet — it loads fresh when it is
      const d = PAGE_DEPS[id];
      if ((moved.ds && d.ds) || (moved.r && d.r) || (moved.b && d.b)) h.refresh();
    }
  }, []);

  /** [JavaScript.html pollStatus] */
  const poll = useCallback(() => {
    if (typeof document !== 'undefined' && document.hidden) return;
    fetchLgpStatus().then((s) => noteVersion(s.version)).catch(() => { /* the next poll tries again */ });
  }, [noteVersion]);
  useEffect(() => {
    const t = window.setInterval(poll, POLL_MS);
    const onVis = () => { if (!document.hidden) poll(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { window.clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [poll]);
  useRealtime(['leadGenPerf'], poll);

  /** [JavaScript.html patchPerson] A roster change applied at once to the two views the roster feeds. */
  const patchPerson = useCallback((p: PersonPatch) => {
    handles.current['leads-count']?.patchPerson?.(p);
    handles.current['leads-analysis']?.patchPerson?.(p);
  }, []);
  /** [JavaScript.html patchLeadsBonus] New bonus rules on Leads Count at once. */
  const patchBonus = useCallback((rules: { from: unknown; to: unknown; amount: unknown }[]) => {
    handles.current['leads-count']?.patchBonus?.(rules);
  }, []);

  const common = { noDock, storageKey, onVersion: noteVersion, register };
  const pill = <ViewPill value={view} onPick={setView} />;
  return (
    <Wrap data-lead-generator-performance="">
      <TopBar>
        <Title>Lead Generator Performance</Title>
        {isMobile ? <PillRow data-lgp-pill-row="">{pill}</PillRow> : pill /* [PHONE-LAYOUT 2026-10-23] the laptop: as before */}
        <span />
      </TopBar>
      <Viewport>
        {VIEWS.map((v) => (
          <SlidePane key={v.id} $pos={slidePos(view, v.id, ORDER)} $quiet $flat data-lgp-pane={v.id} aria-hidden={view !== v.id}>
            {v.id === 'leads-count' && <LeadsCountView active={view === v.id} {...common} />}
            {v.id === 'leads-analysis' && <MonthlyView active={view === v.id} {...common} />}
            {v.id === 'nal-records' && <NalView active={view === v.id} {...common} />}
            {v.id === 'team-roster' && <RosterView active={view === v.id} {...common} onPatchPerson={patchPerson} />}
            {v.id === 'bonus-rules' && <BonusView active={view === v.id} {...common} onPatchBonus={patchBonus} />}
          </SlidePane>
        ))}
      </Viewport>
    </Wrap>
  );
};

/** [PURE-INK 2026-10-15] The whole tab under the pure-ink theme: white / black text, no grey or faded tone (components/PureInk). */
const LeadGeneratorPerformancePureInk: React.FC = () => <PureInk><LeadGeneratorPerformancePage /></PureInk>;

export default LeadGeneratorPerformancePureInk;
